import React, { useEffect, useRef, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

const DonutChart = ({ title, icon: Icon, data, categoryField, valueField }) => {
  const chartRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!chartRef.current) return;

    // Create root element
    let root = am5.Root.new(chartRef.current);

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    let chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        radius: am5.percent(90),
        innerRadius: am5.percent(50),
        layout: root.horizontalLayout
      })
    );

    // Create series
    let series = chart.series.push(
      am5percent.PieSeries.new(root, {
        name: "Series",
        valueField: valueField,
        categoryField: categoryField
      })
    );

    // Neon color palette
    const neonColors = [
      am5.color(0xb24bf3), // purple
      am5.color(0x00e5ff), // cyan
      am5.color(0xff6b35), // orange
      am5.color(0x39ff14), // green
      am5.color(0xffff00), // yellow
      am5.color(0xb24bf3), // purple (repeat for more slices)
      am5.color(0x00e5ff), // cyan
      am5.color(0xff6b35), // orange
      am5.color(0x39ff14), // green
    ];

    series.get("colors").set("colors", neonColors);

    // Set data
    series.data.setAll(data);

    // Disabling labels and ticks
    series.labels.template.set("visible", false);
    series.ticks.template.set("visible", false);

    // Adding gradients for neon effect
    series.slices.template.setAll({
      strokeOpacity: 0,
      fillGradient: am5.RadialGradient.new(root, {
        stops: [{
          brighten: -0.8
        }, {
          brighten: -0.8
        }, {
          brighten: -0.5
        }, {
          brighten: 0
        }, {
          brighten: -0.5
        }]
      }),
      tooltipText: "{category}: {value}"
    });

    // Hover effect with neon glow
    series.slices.template.states.create("hover", {
      scale: 1.05,
      shadowBlur: 20,
      shadowOpacity: 0.8
    });

    // Create legend
    let legend = chart.children.push(
      am5.Legend.new(root, {
        centerY: am5.percent(50),
        y: am5.percent(50),
        layout: root.verticalLayout,
        paddingLeft: 15,
        paddingRight: 15
      })
    );

    // Style legend labels with neon colors
    legend.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(0x00e5ff), // cyan
      fontWeight: "500"
    });

    // Style legend value labels
    legend.valueLabels.template.setAll({
      fontSize: 12,
      fill: am5.color(0xb24bf3), // purple
      fontWeight: "600"
    });

    // Style legend markers with glow effect
    legend.markers.template.setAll({
      width: 12,
      height: 12
    });

    legend.data.setAll(series.dataItems);

    // Set background
    chart.set('background', am5.Rectangle.new(root, {
      fill: am5.color(0x141414),
      fillOpacity: 0
    }));

    // Animate on load
    series.appear(1000, 100);

    // Mark as loaded
    setTimeout(() => setIsLoading(false), 500);

    return () => {
      root.dispose();
    };
  }, [data, categoryField, valueField]);

  return (
    <div className="relative bg-dark-card border border-dark-border rounded-lg p-4 sm:p-6 overflow-hidden group"
         style={{
           background: '#141414',
           boxShadow: '0 0 2px rgba(0, 229, 255, 0.3)'
         }}>

      {/* Background Icon */}
      {Icon && (
        <div className="absolute -right-8 -top-8 opacity-[0.04] text-cyan-accent pointer-events-none"
             style={{ filter: 'drop-shadow(0 0 25px currentColor) drop-shadow(0 0 50px currentColor)' }}>
          <Icon size={150} strokeWidth={1} />
        </div>
      )}

      {/* Header */}
      {title && (
        <div className="relative z-10 mb-4">
          <div className="flex items-center space-x-3 mb-2">
            {Icon && (
              <Icon className="text-cyan-accent" size={24} strokeWidth={2.5}
                   style={{ filter: 'drop-shadow(0 0 10px currentColor) drop-shadow(0 0 20px currentColor)' }} />
            )}
            <h3 className="text-lg sm:text-xl font-bold text-cyan-accent"
                style={{ textShadow: '0 0 20px currentColor, 0 0 40px currentColor' }}>
              {title}
            </h3>
          </div>
        </div>
      )}

      {/* amCharts Donut Chart */}
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="text-cyan-accent animate-pulse">Loading chart...</div>
          </div>
        )}
        <div
          ref={chartRef}
          className="relative z-10 w-full"
          style={{
            height: '400px',
            minHeight: '300px'
          }}
        />
      </div>

      {/* Neon glow border effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none text-cyan-accent rounded-lg"
           style={{ boxShadow: 'inset 0 0 20px currentColor, 0 0 20px currentColor' }} />
    </div>
  );
};

export default DonutChart;

